import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppBar, Box, Typography, Grid, Tab, Tabs } from "@mui/material/";
import actions from "../../../Store/actions";
import styles from "./styles";
import { models } from "powerbi-client";
import TabPanel from "./TabPanel";
import { logToSentry } from "../../../config/Sentry";
import { strings } from "../../i18n";

import { polyfillLoader } from "polyfill-io-feature-detection";

// This function load polyfills only if needed. By default it uses polyfill.io
polyfillLoader({
    features: "Promise"
});

/**
 * PowerBI Reports Component
 */
export default ({ page, reportTabs, reportList }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    const { userOrganisations, auditGroups } = useSelector((state) => state);
    const organisationId = userOrganisations.item.id;

    const [poweBiConfig, setPoweBiConfig] = useState({
        value: 0,
        reportId: reportList[0].reportId,
        datasetId: reportList[0].datasetId
    });

    /** On tab change */
    const onTabsChange = async (event, value) => {
        try {
            // If this tab is custom skip fetching token
            if (reportTabs?.[value]?.isCustom) {
                setPoweBiConfig((prev) => ({ ...prev, value }));
                return;
            }
            setPoweBiConfig({
                value: value,
                reportId: reportList[value].reportId,
                datasetId: reportList[value].datasetId
            });
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry(`errors.onChangingTab: ${reportTabs[value]?.label}`, { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onChangingTab", {
                        name: reportTabs[value].label
                    })
                )
            );
        }
    };

    /** Get latest audit group */
    const latestAuditGroup = useMemo(() => {
        const { data } = auditGroups;
        return data.sort(
            (a, b) => new Date(b.startDate) - new Date(a.startDate)
        )[0];
    }, [auditGroups.data]);

    /** Generate filters for org */
    const generateFilterForOrg = (tableName, columnName) => {
        return {
            $schema: `http://powerbi.com/product/schema#basic`,
            target: {
                table: tableName,
                column: columnName
            },
            operator: "In",
            values: [organisationId],
            filterType: models.FilterType.BasicFilter,
            requireSingleSelection: true
        };
    };

    /** Filter for organisation group  */
    const organisationFilter = generateFilterForOrg(
        "Organisation",
        "Organisation Id"
    );

    /** Filter for audit group  */
    const organisationForAuditGroupFilter = generateFilterForOrg(
        "Organisation for Audit Group",
        "Organisation Id"
    );

    /** Slicer  filters for Audit Group */
    const slicersAuditGroup = (visualId) => {
        if (latestAuditGroup) {
            return [
                {
                    selector: {
                        $schema: `http://powerbi.com/product/schema#visualSelector`,
                        visualName: visualId
                    },
                    state: {
                        filters: [
                            {
                                $schema: `http://powerbi.com/product/schema#basic`,
                                target: {
                                    table: "Audit Group",
                                    column: "Audit Group Id"
                                },
                                operator: "In",
                                values: [latestAuditGroup.id],
                                filterType: models.FilterType.BasicFilter
                            }
                        ]
                    }
                }
            ];
        }
    };

    const [reportEmbedConfig, setReportConfig] = useState({
        type: "report",
        id: undefined,
        embedUrl: `https://app.powerbi.com/reportEmbed?reportId=`,
        accessToken: undefined,
        tokenType: models.TokenType.Embed,
        filters: [organisationFilter, organisationForAuditGroupFilter],
        slicers: null,
        settings: {
            panes: {
                filters: {
                    expanded: false,
                    visible: false
                },
                pageNavigation: {
                    visible: false
                }
            },
            background: models.BackgroundType.Transparent
        }
    });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setLoadError(false);
                //const { value } = poweBiConfig;
                //const selectedReport = reportList[value];
                const { value } = poweBiConfig;
                // If current tab is custom, skip fetching PBI token entirely              
                if (reportTabs?.[value]?.isCustom) {
                  if (mounted) setLoading(false);
                  return;
                }
                const selectedReport = reportList[value];


                // Handle array or string
                const reportIds = Array.isArray(selectedReport.reportId)
                    ? selectedReport.reportId
                    : [selectedReport.reportId];
                const datasetIds = Array.isArray(selectedReport.datasetId)
                    ? selectedReport.datasetId
                    : [selectedReport.datasetId];

                /* Slicers are only for auditGroup and Service Performance */
                const slicers = reportTabs[value].visuals
                    ? slicersAuditGroup(reportTabs[value].visuals)
                    : null;
                const pbiPost = {
                    datasets_id: datasetIds[0],
                    reports_id: reportIds,
                    organisation_id: organisationId,
                    send_identities: selectedReport.hasOwnProperty(
                        "send_identities"
                    )
                        ? selectedReport.send_identities
                        : undefined
                };

                await dispatch(actions.auditGroups.create.getRequest());
                const accessTokenDispatch = await dispatch(
                    actions.powerbi.create.postRequest(pbiPost)
                );

                /** Add  required configurations for report*/
                if (mounted) {
                    setReportConfig({
                        ...reportEmbedConfig,
                        id: reportIds[0],
                        slicers: slicers,
                        accessToken: accessTokenDispatch
                    });
                    setLoading(false);
                }
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("on loading report", { error, errStr });
                // Without clearing loading here the spinner state never ends,
                // and the single-tab branch below would still mount the embed
                // with no token.
                if (mounted) {
                    setLoadError(true);
                    setLoading(false);
                }
                dispatch(
                    actions.toast.create.error(strings("errors.onLoadReport"))
                );
            }
        })();

        /* Cancel all subscriptions and asynchronous tasks in a useEffect 
        cleanup function */
        return () => (mounted = false);
    }, [poweBiConfig]);

    const reportError = (
        <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
                {strings("errors.onLoadReport")}
            </Typography>
        </Box>
    );

    return (
        <Grid container sx={styles.pageMainContainer}>
            {reportTabs.length > 1 ? (
                <Grid item xs={12}>
                    <Box sx={styles.mainHeaderContainer}>
                        <Typography variant="h6" sx={styles.tabHeader}>
                            {strings(page)}
                        </Typography>
                    </Box>

                    <Tabs
                        indicatorColor="primary"
                        onChange={onTabsChange}
                        textColor="primary"
                        value={poweBiConfig.value}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {reportTabs.map((item, index) => (
                            <Tab
                                key={`tab-${item.label}-${index}`}
                                label={strings(item.label)}
                                id={`report-tab-${index}`}
                                aria-controls={`report-tabpanel-${index}`}
                            />
                        ))}
                    </Tabs>

                    {/* Load power bi reports */}
                    {loadError && reportError}
                    {!loadError &&
                        !loading &&
                        reportTabs.map((item, index) => (
                            <TabPanel
                                key={`panel-${item.label}-${index}`}
                                value={poweBiConfig.value}
                                index={index}
                                reportEmbedConfig={reportEmbedConfig}
                                isCustom={item.isCustom}
                                customComponent={item.component}
                            />
                        ))}
                </Grid>
            ) : loadError ? (
                reportError
            ) : (
                <TabPanel
                    key={`panel-${reportTabs.label}-`}
                    value={poweBiConfig.value}
                    index={0}
                    reportEmbedConfig={reportEmbedConfig}
                    isCustom={reportTabs?.[0]?.isCustom}
                    customComponent={reportTabs?.[0]?.component}

                />
            )}
        </Grid>
    );
};
